import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { todayISO } from "../lib/format";

// useDaftar — cari, urut, saringan, paginasi, ekspor untuk SATU daftar.
// Tahap 2 + 4 kematangan ERP (2 Sep 2026); diperluas jadi POLA DAFTAR SERAGAM
// (27 Sep 2026, antrean "PAGINASI & DAFTAR SERAGAM ALA ERP MAPAN"). Panduan
// pemakaiannya: `docs/POLA_DAFTAR.md`.
//
// Yang BARU sejak 27 Sep 2026 (semuanya aditif — pemanggil lama tetap jalan):
//   * SELURUH keadaan daftar tinggal di ALAMAT HALAMAN (`?h=2&n=50&q=…&urut=…&arah=…`
//     + saringan per layar). Tombol Kembali dari halaman rincian mengembalikan
//     posisi orang persis di halaman, ukuran, kata cari, dan urutan yang sama.
//   * Pilihan 25/50/100 baris per halaman (`setUkuran`).
//   * "Muat lagi" untuk layar sempit (`muatLagi`) — halaman berikutnya DITAMBAHKAN
//     ke daftar yang sudah tampil, bukan menggantikannya.
//   * Saringan per layar (`saringan`) lengkap dengan SARINGAN BAWAAN: dipasang
//     saat alamat belum menyebutnya, bisa dihapus orang (dan tetap terhapus).
//
// Bentuk `kolom` pada ekspor / pengurutan: { kunci, ambil?, nilai?, judul? }
//   ambil(b)  -> teks yang ditampilkan / diekspor
//   nilai(b)  -> nilai pembanding untuk mengurut (default: b[kunci])

function nilaiDari(b, kunci, kolom) {
  const k = kolom?.find?.((c) => c.kunci === kunci);
  if (k?.nilai) return k.nilai(b);
  if (k?.ambil && !(kunci in (b || {}))) return k.ambil(b);
  return b?.[kunci];
}

function banding(a, b) {
  const kosongA = a === null || a === undefined || a === "";
  const kosongB = b === null || b === undefined || b === "";
  if (kosongA && kosongB) return 0;
  if (kosongA) return 1;   // yang kosong SELALU di bawah, apa pun arahnya
  if (kosongB) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  if (typeof a === "boolean" && typeof b === "boolean") return (a ? 1 : 0) - (b ? 1 : 0);
  return String(a).localeCompare(String(b), "id", { numeric: true, sensitivity: "base" });
}

// ---------------------------------------------------------------------------
// ALAMAT HALAMAN — parameter pendek yang SAMA di semua layar
//
//   h     halaman (tidak ditulis bila 1)
//   n     baris per halaman (tidak ditulis bila sama dengan bawaan layar)
//   q     kata cari
//   urut  kolom urut, `arah` = asc|desc (tidak ditulis bila sama dengan bawaan)
//   <saringan>  kunci saringan layar itu sendiri, mis. `status`
//   hmuat halaman PERTAMA rangkaian "Muat lagi" (aditif, 28 Sep 2026) — ditulis hanya
//         selama daftar menumpuk; `h` tetap halaman terakhir. Memuat ulang halaman
//         memulihkan rangkaian hmuat..h, bukan hanya halaman h.
//
// Saringan ber-BAWAAN: kunci yang TIDAK ADA di alamat = pakai bawaan; kunci yang
// ADA tapi KOSONG (`?status=`) = orang sengaja menghapusnya. Tanpa pembedaan ini
// saringan bawaan akan terpasang lagi tiap kali orang menghapusnya.
//
// `awalan` dipakai bila satu layar punya DUA daftar (mis. "ret_" → `ret_h`).
// ---------------------------------------------------------------------------
export const UKURAN_PILIHAN = [25, 50, 100];
const UKURAN_MAKS = 200;   // cermin `paginasi.BATAS_HALAMAN` backend

function angkaPositif(teks, cadangan) {
  const n = Number.parseInt(teks, 10);
  return Number.isFinite(n) && n >= 1 ? n : cadangan;
}

/** Keadaan daftar dari URLSearchParams. Murni — dipakai hook dan test. */
export function bacaStateUrl(sp, { awalan = "", ukuranAwal = 25, urutAwal = null, saringan = {} } = {}) {
  const k = (x) => `${awalan}${x}`;
  const n = angkaPositif(sp.get(k("n")), ukuranAwal);
  const urutKunci = sp.get(k("urut"));
  const arah = sp.get(k("arah")) === "desc" ? "desc" : "asc";
  const nilaiSaringan = {};
  Object.entries(saringan || {}).forEach(([kunci, def]) => {
    const kk = k(kunci);
    nilaiSaringan[kunci] = sp.has(kk) ? (sp.get(kk) || "") : (def?.bawaan ?? "");
  });
  return {
    halaman: angkaPositif(sp.get(k("h")), 1),
    ukuran: Math.min(n, UKURAN_MAKS),
    cari: sp.get(k("q")) || "",
    urut: urutKunci ? { kunci: urutKunci, arah } : urutAwal,
    nilaiSaringan,
  };
}

/** Halaman pertama rangkaian "Muat lagi" dari alamat (`hmuat`), atau null bila
 *  tidak ada/tidak sah (harus < halaman). Murni. Terpisah dari `bacaStateUrl`
 *  supaya bentuk keluaran fungsi itu tidak berubah (aditif). */
export function bacaMuatUrl(sp, { awalan = "" } = {}) {
  const halaman = angkaPositif(sp.get(`${awalan}h`), 1);
  const m = angkaPositif(sp.get(`${awalan}hmuat`), null);
  return m !== null && m < halaman ? m : null;
}

/** Rangkaian yang dipulihkan dari alamat dijepit ke batas satu permintaan server
 *  (200 baris): paling banyak floor(200 / ukuran) halaman terakhir. Murni. */
export function jepitMuat(awal, halaman, ukuran) {
  if (awal === null || awal === undefined || awal >= halaman) return null;
  const maksHalaman = Math.max(1, Math.floor(UKURAN_MAKS / Math.max(1, ukuran)));
  const a = Math.max(awal, halaman - maksHalaman + 1);
  return a < halaman ? a : null;
}

/** Tulis perubahan (sebagian) ke salinan URLSearchParams. Murni. Nilai yang sama
 *  dengan bawaan TIDAK ditulis — alamat tetap pendek dan bisa dibagikan. */
export function tulisStateUrl(sp, ubah, { awalan = "", ukuranAwal = 25, urutAwal = null, saringan = {} } = {}) {
  const hasil = new URLSearchParams(sp);
  const k = (x) => `${awalan}${x}`;
  const setAtauHapus = (kunci, nilai, hapusBila) => {
    if (nilai === undefined) return;
    if (nilai === null || nilai === "" || nilai === hapusBila) hasil.delete(k(kunci));
    else hasil.set(k(kunci), String(nilai));
  };
  if ("halaman" in ubah) setAtauHapus("h", ubah.halaman, 1);
  // Rangkaian "Muat lagi": hanya `muatLagi` yang menulis `muat`; perpindahan halaman
  // lain (termasuk cari/ukuran/urut/saringan yang kembali ke halaman 1) memutusnya.
  if ("muat" in ubah) setAtauHapus("hmuat", ubah.muat, null);
  else if ("halaman" in ubah) hasil.delete(k("hmuat"));
  if ("ukuran" in ubah) setAtauHapus("n", ubah.ukuran, ukuranAwal);
  if ("cari" in ubah) setAtauHapus("q", (ubah.cari || "").trim(), "");
  if ("urut" in ubah) {
    const u = ubah.urut;
    const samaBawaan = (!u && !urutAwal)
      || (u && urutAwal && u.kunci === urutAwal.kunci && (u.arah || "asc") === (urutAwal.arah || "asc"));
    if (samaBawaan || !u) { hasil.delete(k("urut")); hasil.delete(k("arah")); }
    else { hasil.set(k("urut"), u.kunci); hasil.set(k("arah"), u.arah === "desc" ? "desc" : "asc"); }
  }
  Object.entries(ubah.saringan || {}).forEach(([kunci, nilai]) => {
    const def = saringan?.[kunci] || {};
    const kk = k(kunci);
    const v = nilai === null || nilai === undefined ? "" : String(nilai);
    const bawaan = def.bawaan ?? "";
    if (v === bawaan) hasil.delete(kk);          // sama dengan bawaan → tidak perlu ditulis
    else if (v === "") hasil.set(kk, "");        // bawaan DIHAPUS orang → tandai eksplisit
    else hasil.set(kk, v);
  });
  return hasil;
}

// ---------------------------------------------------------------------------
// DUA MODE, dan memilih yang salah punya biaya nyata
//
//   KLIEN  (bawaan)  — `baris` sudah memuat SELURUH himpunan. Cari/urut/potong
//                      dikerjakan di sini. Benar SELAMANYA untuk master data:
//                      jenis kerah, kelompok harga, hari libur — himpunan yang
//                      terbatas secara struktur, bukan kebetulan.
//
//   SERVER (`server`) — `baris` cuma SATU HALAMAN, dan `server.total` jumlah
//                      sesungguhnya yang berhak dilihat pemanggil. Cari, urut,
//                      saringan, dan halaman dikirim ke backend.
//
// ⛔ Mode klien di atas data transaksi adalah PEMOTONGAN SENYAP menunggu
//    giliran: endpoint-nya punya `limit` bawaan, layarnya tidak tahu, dan
//    daftarnya tampak lengkap saat ia sudah tidak lengkap.
// ---------------------------------------------------------------------------
export function useDaftar(baris, { kunciCari = [], kolom = [], urutAwal = null,
                                   ukuran: ukuranAwal = 25, server = null,
                                   url = true, awalan = "", saringan = {} } = {}) {
  const [spUrl, setSpUrl] = useSearchParams();
  // `url: false` — daftar di dalam dialog/panel yang TIDAK boleh mengotori alamat
  // halaman induknya. Mesinnya sama; tempat simpannya saja yang lokal.
  const [spLokal, setSpLokal] = useState(() => new URLSearchParams());
  const sp = url ? spUrl : spLokal;

  // Kunci teks untuk definisi saringan: objek `saringan` biasanya ditulis langsung di
  // pemanggilan (objek baru tiap render) — memo yang bergantung padanya akan dihitung
  // ulang tiap render, dan `paramSaringan` yang berubah identitas tiap render memicu
  // muat ulang TANPA HENTI di layar yang memakainya sebagai dependensi.
  const defSaringan = saringan || {};
  const kunciSaringan = JSON.stringify(Object.entries(defSaringan)
    .map(([k, d]) => [k, d?.bawaan ?? "", d?.param || k]));
  const urutAwalKunci = urutAwal?.kunci ?? null;
  const urutAwalArah = urutAwal?.arah === "desc" ? "desc" : "asc";
  const opsiUrl = useMemo(() => ({
    awalan,
    ukuranAwal,
    urutAwal: urutAwalKunci ? { kunci: urutAwalKunci, arah: urutAwalArah } : null,
    saringan: Object.fromEntries(JSON.parse(kunciSaringan).map(([k, b]) => [k, { bawaan: b }])),
  }), [awalan, ukuranAwal, urutAwalKunci, urutAwalArah, kunciSaringan]);
  const opsiRef = useRef(opsiUrl);
  opsiRef.current = opsiUrl;

  const st = useMemo(() => bacaStateUrl(sp, opsiUrl), [sp, opsiUrl]);
  const { halaman, ukuran, urut, nilaiSaringan } = st;

  // Satu tulisan per aksi. Beberapa perubahan sekaligus (mis. kata cari + halaman 1)
  // WAJIB lewat satu panggilan: dua `setSearchParams` di detak yang sama saling timpa.
  const ubahUrl = useCallback((perubahan) => {
    const fn = (prev) => tulisStateUrl(prev, perubahan, opsiRef.current);
    if (url) setSpUrl(fn, { replace: true });
    else setSpLokal(fn);
  }, [url, setSpUrl]);

  // ---- Muat lagi (layar sempit) ------------------------------------------
  // `awalGulir` = halaman pertama rangkaian yang sedang ditampilkan bertumpuk.
  // Mode server menyimpan baris halaman-halaman sebelumnya di `simpanan`; baris
  // halaman yang sedang dimuat baru dianggap tiba begitu referensi `baris` berubah.
  //
  // PEMULIHAN (aditif, 28 Sep 2026): alamat menyimpan `hmuat` (halaman pertama
  // rangkaian). Saat layar dibuka dengan `hmuat`, rangkaian dipulihkan: mode klien
  // cukup memotong dari `hmuat`; mode server meminta SATU rentang gabungan
  // (`paramServer` offset/limit mencakup hmuat..h, paling banyak 200 baris) selama
  // `gabung` — `baris` yang tiba sudah seluruh rangkaian. Aksi apa pun memutusnya;
  // "Muat lagi" berikutnya memecah rentang gabungan ke `simpanan` per halaman.
  const [awalGulir, setAwalGulir] = useState(() => jepitMuat(bacaMuatUrl(sp, { awalan }),
    angkaPositif(sp.get(`${awalan}h`), 1), bacaStateUrl(sp, { awalan, ukuranAwal }).ukuran));
  const [gabung, setGabung] = useState(() => !!server && awalGulir !== null);
  const simpanan = useRef({});
  const barisSaatMuatLagi = useRef(null);
  const resetGulir = useCallback(() => {
    setAwalGulir(null);
    setGabung(false);
    simpanan.current = {};
    barisSaatMuatLagi.current = null;
  }, []);
  useEffect(() => {
    // Alamat mundur ke sebelum rangkaian (mis. tombol Kembali) → rangkaian batal.
    if (awalGulir !== null && halaman < awalGulir) resetGulir();
  }, [halaman, awalGulir, resetGulir]);

  // ---- Kata cari -----------------------------------------------------------
  // Kotak ketik memakai state lokal (responsif); alamat ditulis sesudah JEDA 250 ms.
  // ⛔ Tanpa jeda tiap huruf jadi satu permintaan HTTP di mode server — mengetik
  //    "FASBY-2609" mengirim SEPULUH kueri, dan jawaban yang tiba tak berurutan
  //    bisa menampilkan hasil untuk kata yang sudah tidak diketik lagi.
  const [cari, setCariLokal] = useState(st.cari);
  const cariUrl = st.cari;
  useEffect(() => {
    if (cari.trim() === cariUrl) return undefined;
    const t = setTimeout(() => { resetGulir(); ubahUrl({ cari, halaman: 1 }); }, 250);
    return () => clearTimeout(t);
  }, [cari, cariUrl, ubahUrl, resetGulir]);
  // Alamat berubah dari luar (tombol Kembali/Maju) → kotak ketik ikut.
  const cariUrlSebelum = useRef(cariUrl);
  useEffect(() => {
    if (cariUrlSebelum.current !== cariUrl) {
      cariUrlSebelum.current = cariUrl;
      setCariLokal((c) => (c.trim() === cariUrl ? c : cariUrl));
    }
  }, [cariUrl]);
  const setCari = useCallback((v) => setCariLokal(v ?? ""), []);

  // ---- Mode klien: saring + urut ----------------------------------------
  const modeServer = !!server;
  const tersaring = useMemo(() => {
    const q = cari.trim().toLowerCase();
    const sumber = Array.isArray(baris) ? baris : [];
    if (modeServer || !q || kunciCari.length === 0) return sumber;
    return sumber.filter((b) =>
      kunciCari.some((k) => {
        const v = typeof k === "function" ? k(b) : nilaiDari(b, k, kolom);
        return v !== null && v !== undefined && String(v).toLowerCase().includes(q);
      })
    );
  }, [baris, cari, kunciCari, kolom, modeServer]);

  const terurut = useMemo(() => {
    if (modeServer || !urut?.kunci) return tersaring;
    const arah = urut.arah === "desc" ? -1 : 1;
    // salin dulu — sort() mengubah di tempat, dan `tersaring` bisa jadi `baris` asli
    return [...tersaring].sort((x, y) => {
      const va = nilaiDari(x, urut.kunci, kolom);
      const vb = nilaiDari(y, urut.kunci, kolom);
      const ka = va === null || va === undefined || va === "";
      const kb = vb === null || vb === undefined || vb === "";
      // Yang kosong SELALU di bawah, apa pun arahnya — diputus SEBELUM dikali arah.
      if (ka && kb) return 0;
      if (ka) return 1;
      if (kb) return -1;
      return banding(va, vb) * arah;
    });
  }, [tersaring, urut, kolom, modeServer]);

  // Di mode server, `baris` SUDAH satu halaman hasil saring+urut backend —
  // menyaringnya lagi di sini akan membuang baris yang sah.
  const total = modeServer ? (server.total ?? 0) : terurut.length;
  const totalHalaman = Math.max(1, Math.ceil(total / ukuran));

  // Halaman di luar jangkauan (mis. alamat lama, atau data menyusut) → halaman terakhir.
  // ⚠ Hanya bila total > 0: sebelum data pertama tiba total = 0, dan menjepit saat itu
  //   akan menghapus `h=3` dari alamat yang justru sedang dipulihkan.
  useEffect(() => {
    if (total > 0 && halaman > totalHalaman) { resetGulir(); ubahUrl({ halaman: totalHalaman }); }
  }, [total, halaman, totalHalaman, ubahUrl, resetGulir]);

  // Simpan halaman server yang sudah tiba selama rangkaian "Muat lagi".
  if (modeServer && awalGulir !== null && !gabung && Array.isArray(baris)
      && baris !== barisSaatMuatLagi.current) {
    simpanan.current[halaman] = baris;
  }

  const tampil = useMemo(() => {
    const mulai = awalGulir ?? halaman;
    if (!modeServer) return terurut.slice((mulai - 1) * ukuran, halaman * ukuran);
    const sekarang = Array.isArray(baris) ? baris : [];
    if (awalGulir === null || gabung) return sekarang;   // gabung: `baris` = seluruh rangkaian
    const out = [];
    for (let p = mulai; p < halaman; p += 1) out.push(...(simpanan.current[p] || []));
    if (sekarang !== barisSaatMuatLagi.current) out.push(...sekarang);
    return out;
  }, [modeServer, baris, terurut, halaman, ukuran, awalGulir, gabung]);

  // ---- Aksi ----------------------------------------------------------------
  const setHalaman = useCallback((h) => {
    resetGulir();
    ubahUrl({ halaman: Math.max(1, Number(h) || 1) });
  }, [ubahUrl, resetGulir]);

  const setUkuran = useCallback((n) => {
    resetGulir();
    ubahUrl({ ukuran: Math.min(UKURAN_MAKS, Math.max(1, Number(n) || ukuranAwal)), halaman: 1 });
  }, [ubahUrl, resetGulir, ukuranAwal]);

  const setUrut = useCallback((u) => {
    resetGulir();
    ubahUrl({ urut: typeof u === "function" ? u(urut) : u, halaman: 1 });
  }, [ubahUrl, resetGulir, urut]);

  const toggleUrut = useCallback((kunci) => {
    setUrut(urut?.kunci === kunci
      ? { kunci, arah: urut.arah === "asc" ? "desc" : "asc" }
      : { kunci, arah: "asc" });
  }, [setUrut, urut]);

  const setSaringan = useCallback((kunci, nilai) => {
    resetGulir();
    ubahUrl({ saringan: { [kunci]: nilai }, halaman: 1 });
  }, [ubahUrl, resetGulir]);

  const hapusSaringan = useCallback((kunci) => setSaringan(kunci, ""), [setSaringan]);

  // Beberapa saringan dalam SATU tulisan alamat (aditif, 28 Sep 2026) — dua
  // `setSaringan` berurutan di detak yang sama saling timpa (lihat `ubahUrl`).
  // Dipakai saringan yang saling mengunci, mis. uang muka "menggantung" ⟂ status.
  const setBeberapaSaringan = useCallback((nilai) => {
    resetGulir();
    ubahUrl({ saringan: { ...(nilai || {}) }, halaman: 1 });
  }, [ubahUrl, resetGulir]);

  const hapusSemuaSaringan = useCallback(() => {
    resetGulir();
    const kosong = {};
    Object.keys(opsiRef.current.saringan || {}).forEach((k) => { kosong[k] = ""; });
    ubahUrl({ saringan: kosong, halaman: 1 });
  }, [ubahUrl, resetGulir]);

  const adaLagi = halaman < totalHalaman;
  const muatLagi = useCallback(() => {
    if (!adaLagi) return;
    const awal = awalGulir ?? halaman;
    if (awalGulir === null) setAwalGulir(halaman);
    if (modeServer) {
      const b = Array.isArray(baris) ? baris : [];
      if (gabung) {
        // Rentang gabungan hasil pemulihan → pecah per halaman.
        for (let p = awal; p <= halaman; p += 1) {
          simpanan.current[p] = b.slice((p - awal) * ukuran, (p - awal + 1) * ukuran);
        }
        setGabung(false);
      } else {
        simpanan.current[halaman] = b;
      }
      barisSaatMuatLagi.current = baris;
    }
    ubahUrl({ halaman: halaman + 1, muat: awal });
  }, [adaLagi, awalGulir, halaman, modeServer, baris, ubahUrl, gabung, ukuran]);

  // ---- Saringan untuk ditampilkan + dikirim --------------------------------
  const saringanAktif = Object.entries(defSaringan)
    .filter(([k]) => (nilaiSaringan[k] ?? "") !== "")
    .map(([k, def]) => ({
      kunci: k,
      label: def?.label || k,
      nilai: nilaiSaringan[k],
      teks: def?.tampil ? def.tampil(nilaiSaringan[k]) : nilaiSaringan[k],
      bawaan: (def?.bawaan ?? "") !== "" && nilaiSaringan[k] === def.bawaan,
    }));

  const paramSaringan = useMemo(() => {
    const p = {};
    JSON.parse(kunciSaringan).forEach(([k, , param]) => {
      const v = nilaiSaringan[k];
      if (v !== undefined && v !== "") p[param] = v;
    });
    return p;
  }, [kunciSaringan, nilaiSaringan]);

  const mulaiTampil = awalGulir ?? halaman;
  const rentang = total === 0
    ? { awal: 0, akhir: 0 }
    : { awal: (mulaiTampil - 1) * ukuran + 1, akhir: Math.min(total, halaman * ukuran) };

  return {
    cari, setCari,
    urut, toggleUrut, setUrut,
    halaman, setHalaman, ukuran, setUkuran, total, totalHalaman,
    tampil,      // baris untuk ditampilkan (satu halaman, atau rangkaian "Muat lagi")
    terurut,     // mode klien: SEMUA baris tersaring+terurut (untuk ekspor)
    kolom,
    modeServer,
    rentang,     // { awal, akhir } nomor baris yang sedang tampil — "1–25 dari N"
    adaLagi, muatLagi, sedangMenumpuk: awalGulir !== null,
    nilaiSaringan, setSaringan, hapusSaringan, hapusSemuaSaringan, saringanAktif,
    setBeberapaSaringan,
    paramSaringan,
    // ⛔ Kolom mana yang BENAR-BENAR bisa diurutkan. Di mode server, backend
    //    punya daftar-izin (`URUT_SAH`) dan diam-diam jatuh ke kolom bawaan
    //    untuk apa pun di luarnya — `ThUrut` memakai ini untuk menolak jadi
    //    tombol, supaya layar tidak menjanjikan yang tidak bisa ditepati server.
    bolehUrut: (kunci) => (!server?.urutSah ? true : server.urutSah.includes(kunci)),
    // Parameter siap kirim ke backend. Halaman memakainya di dependensi
    // `useCallback` load-nya, jadi perubahan cari/urut/halaman memicu muat
    // ulang tanpa satu pun useEffect tambahan di halaman. Saringan ada di
    // `paramSaringan` (nama parameter server per saringan: `def.param`).
    // Selama `gabung` (rangkaian dipulihkan dari `hmuat`): SATU rentang hmuat..h.
    paramServer: {
      cari: cariUrl.trim() || undefined,
      limit: gabung ? (halaman - awalGulir + 1) * ukuran : ukuran,
      offset: ((gabung ? awalGulir : halaman) - 1) * ukuran,
      sort: urut?.kunci,
      arah: urut?.arah,
    },
  };
}

/** Ekspor di MODE SERVER: kumpulkan seluruh hasil saringan halaman demi
 *  halaman, baru tulis CSV.
 *
 *  ⛔ Tanpa ini, tombol Ekspor di mode server akan menulis SATU HALAMAN dan
 *     menamainya seperti daftar lengkap — berkas yang tampak benar, dipakai
 *     menghitung, dan diam-diam kurang.
 *
 *  `ambilHalaman(offset, limit)` -> {items}. Batas 5.000 baris disengaja:
 *  ekspor yang lebih besar dari itu bukan lagi pekerjaan browser — dan bila
 *  tersentuh, pemanggil WAJIB menampilkan `terpotong` (PalangDaftar melakukannya).
 */
export const BATAS_EKSPOR = 5000;

export async function eksporCsvServer(nama, kolom, ambilHalaman, total, ukuran = 200) {
  const mau = Math.min(total ?? 0, BATAS_EKSPOR);
  const semua = [];
  for (let off = 0; off < mau; off += ukuran) {
    // eslint-disable-next-line no-await-in-loop
    const r = await ambilHalaman(off, Math.min(ukuran, mau - off));
    const items = r?.items || [];
    semua.push(...items);
    if (items.length === 0) break;   // backend berhenti memberi — jangan berputar selamanya
  }
  eksporCsv(nama, kolom, semua);
  return { diekspor: semua.length, diminta: mau, total: total ?? 0, terpotong: (total ?? 0) > BATAS_EKSPOR };
}

/** Ekspor CSV yang dibuka benar oleh Excel berbahasa Indonesia: pemisah `;`
 *  (Excel ID memakai koma untuk desimal), BOM UTF-8 supaya huruf berdiakritik
 *  tidak jadi tanda tanya. Yang diekspor adalah `terurut` — seluruh hasil saringan,
 *  BUKAN cuma halaman yang tampil. */
export function eksporCsv(nama, kolom, baris) {
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = typeof v === "number" ? String(v) : String(v).replace(/"/g, '""');
    return /[;"\n\r]/.test(s) ? `"${s}"` : s;
  };
  const kepala = kolom.map((k) => esc(k.judul ?? k.kunci)).join(";");
  const isi = baris.map((b) => kolom.map((k) => esc(k.ambil ? k.ambil(b) : b?.[k.kunci])).join(";"));
  const blob = new Blob(["﻿" + [kepala, ...isi].join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${nama}_${todayISO()}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
